import React from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  ChartBox, CHART_MARGIN, GRID, xAxisProps, yAxisProps,
  KitTooltip, renderRefLines, normaliseSeries, compactAxis, num, EmptyState,
} from "./_kit";

/**
 * Generic area / stacked-area chart.
 * @param {object[]} props.data - one row per x value, keyed by xKey + series keys
 * @param {string} [props.xKey="x"]
 * @param {{key:string,name?:string,color?:string,stackId?:string,fillOpacity?:number}[]} props.series
 * @param {boolean} [props.stacked] - stack all series (or set per-series stackId for groups)
 * @param {{x?:number,y?:number,label?:string}[]} [props.refLines]
 * @param {string} [props.unit]
 */
export function RAreaChart({
  data = [], xKey = "x", series = [], refLines = [], stacked = false,
  unit, height = 320, formatValue = num, formatAxis = compactAxis, curve = "monotone",
  animate = false, animationDuration = 700,
}) {
  if (!data.length || !series.length) return <EmptyState />;
  const items = normaliseSeries(series);

  return (
    <ChartBox $height={height}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey={xKey} {...xAxisProps} />
          <YAxis {...yAxisProps} tickFormatter={formatAxis} />
          <Tooltip content={<KitTooltip unit={unit} formatValue={formatValue} />} />
          {renderRefLines(refLines)}
          {items.map((s) => (
            <Area
              key={s.key}
              type={curve}
              dataKey={s.key}
              name={s.name}
              stackId={s.stackId ?? (stacked ? "stack" : undefined)}
              stroke={s.color}
              strokeWidth={s.strokeWidth ?? 2}
              strokeDasharray={s.dashed ? "5 4" : undefined}
              fill={s.color}
              fillOpacity={s.fillOpacity ?? (stacked ? 0.5 : 0.12)}
              dot={s.dot ?? false}
              connectNulls={s.connectNulls ?? true}
              isAnimationActive={animate}
              animationDuration={animationDuration}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </ChartBox>
  );
}
